import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, DIALOG_MODE_VIEW } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleGetPriceComponents } from '~/thunks/price-components/priceComponentsThunk';
import useDebounce from '~/hooks/useDebounce';

import styles from './PriceItem.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { IPayloadDeletePriceComponent } from '~/utils/interface/estimate';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import { IPriceItem } from '~/utils/interface/priceItem';
import EditPriceItemDialog from '~/components/price-items/EditPriceItem/EditPriceItemDialog';
import { handleGetPrices } from '~/thunks/price/pricesThunk';
import PriceItemList from '~/components/price-items/PriceItemList/PriceItemList';
import { handleGetPriceItem, handleGetPriceItems } from '~/thunks/price-item/priceItemsThunk';
import { priceItemActions } from '~/thunks/price-item/priceItemsSlice';
import priceItemApi from '~/apis/priceItem';

const cx = classNames.bind(styles);

const PriceItem = () => {
  const permissions = usePermission();
  const [searchInput, setSearchInput] = useState<string>('');
  const { t } = useTranslation(['admin/price-item', 'admin/component']);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const [disableItem, setDisableItem] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const searchInputDebounce = useDebounce<string>(searchInput);
  const [editData, setEditData] = useState<IPriceItem>();
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.priceItem
  );

  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
  };

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
    setDisableItem(false);
  };

  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetPriceItem(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
    setDisableItem(false);
    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleOpenViewDiaglog = async (id: string) => {
    const response = await dispatch(handleGetPriceItem(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_VIEW);
    setOpenEditDialog(true);
    setDisableItem(true);
    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(priceItemActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(priceItemActions.changeMenuSelectId(undefined));
    dispatch(priceItemActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedPriceItems = useCallback(async () => {
    let payloadDeletePriceItems: IPayloadDeletePriceComponent =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await priceItemApi.deletePriceItem(payloadDeletePriceItems);
      notify('success', t('deleteSuccessMessage'));
      dispatch(priceItemActions.changeNeedRefreshData(true));
      dispatch(priceItemActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);
  useEffect(() => {
    dispatch(handleGetPrices({ page: 0, limit: 0 }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, needRefreshData]);

  useEffect(() => {
    dispatch(handleGetPriceComponents({ page: 0, limit: 0 }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, needRefreshData]);

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        priceItemActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['code', 'title', 'description'],
          page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetPriceItems(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            {permissions[PERMISSIONS.priceItem_delete] && (
              <Button
                className={cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete')}
              </Button>
            )}
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
        </Box>

        {permissions[PERMISSIONS.priceItem_create] && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpen}
            >
              {t('addNew')}
            </Button>
            <EditPriceItemDialog
              open={openEditDialog}
              setOpen={handleSetOpen}
              mode={editDialogMode}
              data={editData}
              disable={disableItem}
              showFullField={true}
            />
          </Box>
        )}
      </Box>
      <Box className='mt-[40px]'>
        <PriceItemList onOpenEdit={handleOpenEdit} data={editData} onOpenView={handleOpenViewDiaglog} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedPriceItems}
      />
    </Box>
  );
};

export default PriceItem;
